import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useTelephonyProfiles } from '@/entities/telephony'
import { cn, errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CardLink } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { ProfileForm } from '@/widgets/profile-form'

export const Route = createFileRoute('/telephony/profiles')({
  component: ProfilesPage,
  staticData: { crumb: 'Профили' },
})

function ProfilesPage() {
  const { profiles, isPending, isError, error, refetch } = useTelephonyProfiles()
  const { profileId } = useParams({ strict: false })
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <>
      <PageHeader
        title="Профили операторов"
        description="Каталог параметров подключения к операторам связи"
        actions={
          <Button variant="accent" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={18} />
            Завести профиль
          </Button>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить профили"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : profiles.length === 0 ? (
        <EmptyState
          icon="phone"
          title="Профилей пока нет"
          description="Заведи профиль оператора, чтобы регистрировать SIP-учётки"
          action={
            <Button variant="accent" size="sm" onClick={() => setCreateOpen(true)}>
              Завести профиль
            </Button>
          }
        />
      ) : (
        <div className="flex items-start gap-6">
          <div className="flex w-[400px] shrink-0 flex-col gap-2">
            {profiles.map((profile) => {
              const active = profileId === profile.id
              return (
                <CardLink
                  key={profile.id}
                  to="/telephony/profiles/$profileId"
                  params={{ profileId: profile.id }}
                  active={active}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-body-sm font-medium">{profile.name}</span>
                    {profile.retired ? (
                      <StatusBadge tone="muted" label="Выведен" />
                    ) : (
                      <Chip tone="tag">#{profile.market.toUpperCase()}</Chip>
                    )}
                  </div>
                  <p
                    className={cn(
                      'mt-0.5 truncate text-body-sm',
                      active ? 'text-white/80' : 'text-muted',
                    )}
                  >
                    {profile.registrar} · {profile.transport.toUpperCase()}
                  </p>
                </CardLink>
              )
            })}
          </div>

          <div className="min-w-0 flex-1">
            <Outlet />
          </div>
        </div>
      )}

      <ProfileForm open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}
